import { useEffect, useRef } from 'react';

const personas = [
  {
    icon: 'ri-compass-3-line',
    title: 'Está perdida entre tantas opções.',
    description: 'O excesso de informação do mercado de casamentos pode ser paralisante. Você busca clareza e direcionamento, mas cada busca no Pinterest e Instagram só aumenta a confusão. Aqui, você encontra o norte.',
    accent: 'oklch(var(--primary-500))',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Quer fazer escolhas inteligentes sem desperdiçar orçamento.',
    description: 'Você valoriza cada centavo investido e quer tomar decisões baseadas no que realmente importa. Chega de gastar com o que não faz diferença — aqui você aprende a priorizar com sabedoria.',
    accent: 'oklch(var(--accent-500))',
  },
  {
    icon: 'ri-heart-2-line',
    title: 'Deseja criar uma experiência autêntica e memorável.',
    description: 'Seu casamento precisa ter a sua cara, não a cara de um modelo pronto. Você quer que cada detalhe conte uma parte da sua história e que seus convidados sintam exatamente quem vocês são como casal.',
    accent: 'oklch(var(--primary-500))',
  },
];

export default function WhoIsItForSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="para-quem" className="relative py-24 md:py-36 px-6 md:px-10" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 md:mb-24 reveal-on-scroll">
          <span
            className="inline-block text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full border mb-6"
            style={{
              color: 'oklch(var(--secondary-500))',
              borderColor: 'oklch(var(--secondary-300) / 0.5)',
            }}
          >
            Para Quem
          </span>
          <h2
            className="font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.12] mb-6"
            style={{ color: 'oklch(var(--foreground-900))' }}
          >
            Para quem é esta
            <br />
            experiência?
          </h2>
          <p className="text-sm md:text-base max-w-xl mx-auto leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
            O The Future Wedding foi pensado para noivas que não se contentam com o óbvio e
            querem construir algo verdadeiramente significativo.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {personas.map((persona, index) => (
            <div
              key={index}
              className="reveal-on-scroll group p-8 md:p-10 rounded-lg transition-all duration-500 cursor-default relative overflow-hidden flex flex-col"
              style={{
                backgroundColor: 'oklch(var(--background-50))',
                border: '1px solid oklch(var(--background-200) / 0.6)',
                transitionDelay: `${index * 0.12}s`,
              }}
            >
              {/* Top accent dot */}
              <div
                className="w-2 h-2 rounded-full mb-6 transition-all duration-500 group-hover:scale-150"
                style={{ backgroundColor: persona.accent }}
              ></div>

              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110"
                style={{ backgroundColor: 'oklch(var(--background-100))' }}
              >
                <i
                  className={`${persona.icon} text-xl`}
                  style={{ color: persona.accent }}
                ></i>
              </div>

              <p className="text-[10px] tracking-[0.2em] uppercase mb-3 font-semibold" style={{ color: 'oklch(var(--secondary-500))' }}>
                Para a noiva que...
              </p>

              <h3
                className="font-heading text-xl md:text-2xl mb-4 leading-snug flex-grow"
                style={{ color: 'oklch(var(--foreground-900))' }}
              >
                {persona.title}
              </h3>

              <p className="text-sm leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                {persona.description}
              </p>

              {/* Bottom accent bar on hover */}
              <div
                className="absolute bottom-0 left-0 w-full h-0.5 transition-all duration-500 opacity-0 group-hover:opacity-100"
                style={{ backgroundColor: persona.accent }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}